import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import {
  Search, Users, Sparkles, Eye, Send, X,
  GraduationCap, Star, AlertTriangle, User,
  BarChart3, Target, Briefcase, ChevronDown, SlidersHorizontal,
  MapPin, Calendar, Phone, Mail, CheckCircle, XCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import type { Job, CandidateMatch } from '../types'
import type { SalaryRange } from '../types'
import { notifyJobInvitationEmail } from '../utils/emailNotify'
import { EMPLOYMENT_TYPE_LABELS, EXPERIENCE_LEVEL_LABELS, SALARY_LABELS } from '../constants/profileOptions'
import InfoTooltip from '../components/InfoTooltip'
import { CareerTimeline } from '../components/CareerTimeline'

// ── Types ─────────────────────────────────────────────────────
interface AlumniProfile {
  user_id: string
  first_name: string
  last_name: string
  program: string
  department: string
  year_graduated: number
  profile_picture: string | null
  current_employment_status: string | null
  field_of_work: string | null
  skills_acquired: string[] | null
  job_position: string | null
  phone?: string | null
}

// ── Score helpers ─────────────────────────────────────────────
function barColor(score: number) {
  if (score >= 70) return 'bg-green-500'
  if (score >= 40) return 'bg-yellow-500'
  return 'bg-gray-400'
}

/** Mirrors Python _find_matching_skills fuzzy logic (exact, substring, contained) */
function isSkillMatch(alumniSkill: string, matchingSkills: string[] | undefined): boolean {
  if (!matchingSkills || matchingSkills.length === 0) return false
  const al = alumniSkill.toLowerCase()
  return matchingSkills.some(ms => {
    const js = ms.toLowerCase()
    return js === al || js.includes(al) || al.includes(js)
  })
}

const STATUS_LABELS: Record<string, string> = {
  employed: 'Employed',
  unemployed: 'Unemployed',
  'self-employed': 'Self-Employed',
  studying: 'Studying',
  other: 'Other',
}

// ── Component ─────────────────────────────────────────────────
const EmployerFindCandidatesPage: React.FC = () => {
  useAuthStore()
  const [searchParams] = useSearchParams()

  // Browse mode data
  const [allAlumni, setAllAlumni] = useState<AlumniProfile[]>([])
  const [browseLoading, setBrowseLoading] = useState(true)

  // AI matching mode
  const [matchingMode, setMatchingMode] = useState(false)
  const [jobs, setJobs] = useState<Job[]>([])
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)
  const [candidates, setCandidates] = useState<CandidateMatch[]>([])
  const [matchLoading, setMatchLoading] = useState(false)
  const [matchError, setMatchError] = useState<string | null>(null)

  // Filters
  const [search, setSearch] = useState('')
  const [programFilter, setProgramFilter] = useState('all')
  const [departmentFilter, setDepartmentFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [minScore, setMinScore] = useState(0)
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  // Job picker modal
  const [showJobPicker, setShowJobPicker] = useState(false)
  const [jobPickerSearch, setJobPickerSearch] = useState('')

  // Invite modal (AI mode)
  const [inviteTarget, setInviteTarget] = useState<CandidateMatch | null>(null)
  const [inviteMessage, setInviteMessage] = useState('')
  const [inviting, setInviting] = useState(false)

  // Invite modal (Browse mode)
  const [browseInviteTarget, setBrowseInviteTarget] = useState<AlumniProfile | null>(null)
  const [browseInviteJobId, setBrowseInviteJobId] = useState('')
  const [browseInviting, setBrowseInviting] = useState(false)
  const [invitedAlumniIds, setInvitedAlumniIds] = useState<Set<string>>(new Set())
  const [browseAlreadyInvited, setBrowseAlreadyInvited] = useState(false)

  // Program code → full name lookup
  const [programMap, setProgramMap] = useState<Record<string, string>>({})

  // Pagination
  const CANDIDATES_PER_PAGE = 12
  const [candidatePage, setCandidatePage] = useState(1)

  // Profile drawer
  const [viewingAlumni, setViewingAlumni] = useState<AlumniProfile | CandidateMatch | null>(null)
  // undefined = not yet fetched, null = fetched but not found/no email, string = loaded
  const [viewingAlumniEmail, setViewingAlumniEmail] = useState<string | null | undefined>(undefined)

  // ── Check if browse invite target is already invited for selected job ─
  useEffect(() => {
    if (!browseInviteTarget || !browseInviteJobId) {
      setBrowseAlreadyInvited(false)
      return
    }
    // Check local session set first (instant)
    if (invitedAlumniIds.has(browseInviteTarget.user_id)) {
      setBrowseAlreadyInvited(true)
      return
    }
    // Otherwise query database for active invitation
    supabaseClient
      .from('job_invitations')
      .select('id')
      .eq('job_id', browseInviteJobId)
      .eq('alumni_id', browseInviteTarget.user_id)
      .in('status', ['pending', 'accepted'])
      .maybeSingle()
      .then(({ data }) => setBrowseAlreadyInvited(!!data))
  }, [browseInviteTarget, browseInviteJobId, invitedAlumniIds])

  // ── Fetch program names ──────────────────────────────────
  const fetchPrograms = useCallback(async () => {
    try {
      const { data } = await supabaseClient
        .from('programs')
        .select('code, full_name, name')
      if (data) {
        const map: Record<string, string> = {}
        for (const p of data) {
          if (p.code) map[p.code] = p.full_name || p.name || p.code
        }
        setProgramMap(map)
      }
    } catch {}
  }, [])

  // ── Fetch all approved alumni ──────────────────────────────
  const fetchAlumni = useCallback(async () => {
    setBrowseLoading(true)
    try {
      const { data, error } = await supabaseClient
        .from('alumni_profiles')
        .select('user_id, first_name, last_name, program, department, year_graduated, profile_picture, current_employment_status, field_of_work, skills_acquired, job_position, phone')
        .eq('approval_status', 'approved')
        .order('last_name', { ascending: true })

      if (error) throw error
      setAllAlumni((data || []) as AlumniProfile[])
    } catch (err: any) {
      console.error('Failed to load alumni:', err)
      toast.error('Failed to load alumni directory')
    } finally {
      setBrowseLoading(false)
    }
  }, [])

  // ── Fetch employer's jobs ──────────────────────────────────
  const fetchJobs = useCallback(async () => {
    try {
      const { data, error } = await supabaseClient.rpc('get_employer_jobs')
      if (error) throw error
      const activeJobs = ((data || []) as Job[]).filter(j => j.status === 'active')
      setJobs(activeJobs)
    } catch (err: any) {
      console.error('Failed to load jobs:', err)
    }
  }, [])

  useEffect(() => {
    fetchAlumni()
    fetchJobs()
    fetchPrograms()
  }, [fetchAlumni, fetchJobs, fetchPrograms])

  // ── Auto-select job + enable AI mode from query params ─────
  const paramsApplied = useRef(false)
  useEffect(() => {
    if (paramsApplied.current || jobs.length === 0) return
    const jobId = searchParams.get('jobId')
    const ai = searchParams.get('ai')
    if (jobId || ai) {
      paramsApplied.current = true
      if (jobId) setSelectedJobId(jobId)
      if (ai === '1') setMatchingMode(true)
    }
  }, [searchParams, jobs])

  // ── Fetch THEJMA scores for selected job ───────────────────
  const fetchCandidateScores = useCallback(async (jobId: string) => {
    setMatchLoading(true)
    setMatchError(null)
    setCandidates([])
    try {
      const { data: { session } } = await supabaseClient.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('No auth session')

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      const res = await fetch(`${apiUrl}/api/jobs/${jobId}/candidates`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `Server returned ${res.status}`)
      }

      const body = await res.json()
      if (body.success && body.data) {
        const mapped: CandidateMatch[] = body.data.map((c: any) => ({
          alumni_id: c.alumni_id,
          first_name: c.first_name ?? '',
          last_name: c.last_name ?? '',
          program: c.program ?? '',
          department: c.department ?? '',
          year_graduated: c.year_graduated ?? 0,
          profile_picture: c.profile_picture ?? undefined,
          current_employment_status: c.current_employment_status,
          field_of_work: c.field_of_work ?? undefined,
          skills_acquired: c.skills_acquired ?? [],
          peo_alignment_score: c.match.peo_alignment_score,
          skills_score: c.match.skills_score,
          profile_fit_score: c.match.profile_fit_score,
          overall_score: c.match.overall_score,
          matching_skills: c.match.matching_skills,
          matching_outcomes: c.match.matching_outcomes,
          score_type: c.match.score_type,
          has_outcome_scores: c.match.has_outcome_scores,
          is_invited: c.is_invited,
          application_status: (c.application_status ?? null) as CandidateMatch['application_status'],
          has_applied: c.application_status != null,
        }))
        setCandidates(mapped)
      }
    } catch (err: any) {
      console.error('Candidate scoring error:', err)
      setMatchError(err.message || 'Failed to compute match scores')
    } finally {
      setMatchLoading(false)
    }
  }, [])

  // Auto-fetch when job is selected in matching mode
  useEffect(() => {
    if (matchingMode && selectedJobId) {
      fetchCandidateScores(selectedJobId)
    }
  }, [matchingMode, selectedJobId, fetchCandidateScores])

  // Reset when exiting matching mode
  useEffect(() => {
    if (!matchingMode) {
      setMinScore(0)
      setSelectedJobId(null)
      setCandidates([])
      setMatchError(null)
    }
  }, [matchingMode])

  // ── Derived data ───────────────────────────────────────────
  const programs = useMemo(() => {
    const src = matchingMode && candidates.length > 0 ? candidates : allAlumni
    return [...new Set(src.map(a => a.program).filter(Boolean))].sort()
  }, [allAlumni, candidates, matchingMode])

  const departments = useMemo(() => {
    const src = matchingMode && candidates.length > 0 ? candidates : allAlumni
    return [...new Set(src.map(a => a.department).filter(Boolean))].sort()
  }, [allAlumni, candidates, matchingMode])

  // Filtered list
  const filteredAlumni = useMemo(() => {
    const src = matchingMode && candidates.length > 0 ? candidates : allAlumni
    let list = [...src]
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(a =>
        `${a.first_name} ${a.last_name}`.toLowerCase().includes(q) ||
        a.program?.toLowerCase().includes(q) ||
        (a.skills_acquired ?? []).some(s => s.toLowerCase().includes(q))
      )
    }
    if (programFilter !== 'all') list = list.filter(a => a.program === programFilter)
    if (departmentFilter !== 'all') list = list.filter(a => a.department === departmentFilter)
    if (!matchingMode && statusFilter !== 'all') {
      list = list.filter(a => a.current_employment_status === statusFilter)
    }
    if (matchingMode && minScore > 0) {
      list = list.filter(a => 'overall_score' in a && (a as CandidateMatch).overall_score >= minScore)
    }
    return list
  }, [allAlumni, candidates, matchingMode, search, programFilter, departmentFilter, statusFilter, minScore])

  // Reset to page 1 whenever filters change
  useEffect(() => { setCandidatePage(1) }, [search, programFilter, departmentFilter, statusFilter, minScore, matchingMode, selectedJobId])

  const totalCandidatePages = Math.max(1, Math.ceil(filteredAlumni.length / CANDIDATES_PER_PAGE))
  const pagedAlumni = filteredAlumni.slice((candidatePage - 1) * CANDIDATES_PER_PAGE, candidatePage * CANDIDATES_PER_PAGE)

  // Match stats (in AI mode)
  const matchStats = useMemo(() => {
    if (!matchingMode || candidates.length === 0) return null
    const scores = candidates.map(c => c.overall_score)
    return {
      top: Math.max(...scores),
      excellent: scores.filter(s => s >= 75).length,
      good: scores.filter(s => s >= 50).length,
      avg: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
    }
  }, [candidates, matchingMode])

  const selectedJob = jobs.find(j => j.id === selectedJobId)

  // ── Invite handler ─────────────────────────────────────────
  const handleInvite = async () => {
    if (!inviteTarget || !selectedJobId) return
    setInviting(true)
    try {
      const { data, error: rpcErr } = await supabaseClient.rpc('invite_candidate_to_apply', {
        p_job_id: selectedJobId,
        p_alumni_id: inviteTarget.alumni_id,
        p_message: inviteMessage || null,
      })
      if (rpcErr) throw rpcErr
      const result = data as { success: boolean; message: string }
      if (!result.success) { toast.error(result.message); return }

      toast.success('Invitation sent!')
      notifyJobInvitationEmail(inviteTarget.alumni_id, selectedJobId).catch(() => {})
      setCandidates(prev =>
        prev.map(c => c.alumni_id === inviteTarget.alumni_id ? { ...c, is_invited: true } : c)
      )
      setInviteTarget(null)
      setInviteMessage('')
    } catch (err: any) {
      console.error('Invite error:', err)
      toast.error(err.message || 'Failed to send invitation')
    } finally {
      setInviting(false)
    }
  }

  // ── Browse-mode invite handler ────────────────────────────
  const handleBrowseInvite = async () => {
    if (!browseInviteTarget || !browseInviteJobId) return
    setBrowseInviting(true)
    try {
      const { data, error: rpcErr } = await supabaseClient.rpc('invite_candidate_to_apply', {
        p_job_id: browseInviteJobId,
        p_alumni_id: browseInviteTarget.user_id,
        p_message: inviteMessage || null,
      })
      if (rpcErr) throw rpcErr
      const result = data as { success: boolean; message: string }
      if (!result.success) { toast.error(result.message); return }

      toast.success('Invitation sent!')
      notifyJobInvitationEmail(browseInviteTarget.user_id, browseInviteJobId).catch(() => {})
      setInvitedAlumniIds(prev => new Set([...prev, browseInviteTarget.user_id]))
      setBrowseInviteTarget(null)
      setInviteMessage('')
      setBrowseInviteJobId('')
    } catch (err: any) {
      console.error('Browse invite error:', err)
      toast.error(err.message || 'Failed to send invitation')
    } finally {
      setBrowseInviting(false)
    }
  }

  // Helper: determine if item is CandidateMatch
  const isCandidate = (a: AlumniProfile | CandidateMatch): a is CandidateMatch => 'overall_score' in a
  const getId = (a: AlumniProfile | CandidateMatch) => 'alumni_id' in a ? a.alumni_id : (a as AlumniProfile).user_id

  // Open profile modal + fetch email
  const openCandidateProfile = (alumni: AlumniProfile | CandidateMatch) => {
    setViewingAlumni(alumni)
    setViewingAlumniEmail(undefined) // reset to loading state
    const alumniId = getId(alumni)
    supabaseClient.rpc('get_alumni_contact', { p_alumni_id: alumniId })
      .then(
        ({ data }) => {
          const row = Array.isArray(data) ? data[0] : data
          setViewingAlumniEmail(row?.email ?? null)
        },
        () => setViewingAlumniEmail(null)
      )
  }

  // ── Render: Match Dashboard sidebar ────────────────────────
  const renderMatchDashboard = () => (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-bold text-indigo-900 mb-3 uppercase tracking-wide flex items-center gap-1.5">
          <Target className="w-3.5 h-3.5" /> How Scores Work
        </h3>
        <div className="space-y-3">
          {[
            { label: 'PEO/PO Alignment', pct: '50%', color: 'indigo', tooltip: 'Measures how well a candidate\'s academic learning outcomes align with your job requirements. PEOs (Program Educational Objectives) are broad career goals graduates achieve years after graduation; POs (Program Outcomes) are specific skills and competencies demonstrated upon completing their program.' },
            { label: 'Skills Match', pct: '30%', color: 'blue' },
            { label: 'Profile Fit', pct: '20%', color: 'emerald' },
          ].map(item => (
            <div key={item.label}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-600 flex items-center gap-1">{item.label}{'tooltip' in item && <InfoTooltip text={(item as any).tooltip} />}</span>
                <span className={`font-bold text-${item.color}-600`}>{item.pct}</span>
              </div>
              <div className={`h-1.5 bg-${item.color}-100 rounded-full overflow-hidden`}>
                <div className={`h-full bg-${item.color}-500 rounded-full`} style={{ width: item.pct }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-indigo-900 mb-3 uppercase tracking-wide flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5" /> Score Legend
        </h3>
        <div className="space-y-2">
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500" /><span className="text-xs text-gray-700">75%+ — Excellent Match</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500" /><span className="text-xs text-gray-700">50–74% — Good Match</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-gray-400" /><span className="text-xs text-gray-700">Below 50% — Low Match</span></div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-indigo-900 mb-3 uppercase tracking-wide">Filter by Score</h3>
        <div className="space-y-1.5">
          {[
            { label: 'Excellent (75%+)', min: 75, color: 'text-green-600' },
            { label: 'Good (50%+)', min: 50, color: 'text-blue-600' },
            { label: 'All Candidates', min: 0, color: 'text-gray-600' },
          ].map(tier => {
            const count = candidates.filter(c => c.overall_score >= tier.min).length
            return (
              <button
                key={tier.min}
                onClick={() => setMinScore(tier.min)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition ${
                  minScore === tier.min
                    ? 'bg-indigo-50 border border-indigo-200 font-semibold'
                    : 'hover:bg-gray-50 border border-transparent'
                }`}
              >
                <span className={tier.color}>{tier.label}</span>
                <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{count}</span>
              </button>
            )
          })}
        </div>
      </div>

      <button
        onClick={() => setMatchingMode(false)}
        className="w-full py-2.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition flex items-center justify-center gap-1.5"
      >
        <Users className="w-3.5 h-3.5" /> Switch to Browse Mode
      </button>
    </div>
  )

  // ── Render: Browse filters sidebar ─────────────────────────
  const renderFilters = () => (
    <div className="space-y-5">
      <div>
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Program</h3>
        <select
          value={programFilter}
          onChange={e => setProgramFilter(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Programs</option>
          {programs.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div>
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Department</h3>
        <select
          value={departmentFilter}
          onChange={e => setDepartmentFilter(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Departments</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      <div>
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Employment Status</h3>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="pt-2 border-t border-gray-100">
        <button
          onClick={() => { setProgramFilter('all'); setDepartmentFilter('all'); setStatusFilter('all'); setSearch('') }}
          className="w-full py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
        >
          Clear Filters
        </button>
      </div>
    </div>
  )

  // ── Main render ────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* ══════════ Hero Banner ══════════ */}
      <div className={`rounded-2xl p-6 transition-all duration-500 ${
        matchingMode
          ? 'bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-800'
          : 'bg-blue-900'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              {matchingMode ? <Sparkles className="w-6 h-6 text-yellow-300" /> : <Users className="w-6 h-6 text-blue-200" />}
              Find Candidates
            </h1>
            <p className="text-blue-200 text-sm mt-1">
              {matchingMode
                ? 'Candidate matching using THEJMA algorithm'
                : `Browse ${allAlumni.length} approved UCLM alumni`
              }
            </p>
          </div>
          {matchingMode && matchStats && (
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5 text-yellow-200">
                <Target className="w-3.5 h-3.5 text-yellow-300" />
                Top match: {matchStats.top}%
              </div>
              <div className="flex items-center gap-1.5 text-green-200">
                <Star className="w-3.5 h-3.5 text-green-300" />
                {matchStats.excellent} excellent
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ══════════ AI Matching Toggle ══════════ */}
      {jobs.length > 0 && (
        <div
          className={`rounded-2xl border-2 transition-all duration-300 cursor-pointer select-none ${
            matchingMode
              ? 'bg-gradient-to-r from-indigo-50 via-purple-50 to-indigo-50 border-indigo-300 shadow-lg shadow-indigo-100/50'
              : 'bg-white border-gray-200 hover:border-indigo-200 hover:shadow-md'
          }`}
          onClick={() => setMatchingMode(!matchingMode)}
        >
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
                matchingMode ? 'bg-indigo-600 shadow-lg shadow-indigo-300' : 'bg-gray-100'
              }`}>
                <Sparkles className={`w-5 h-5 transition-colors ${matchingMode ? 'text-white' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className={`text-sm font-bold transition-colors ${matchingMode ? 'text-indigo-900' : 'text-gray-700'}`}>
                  Candidate Matching
                  {matchingMode && <span className="ml-1.5 text-[10px] font-bold bg-indigo-600 text-white px-1.5 py-0.5 rounded-full align-middle">ACTIVE</span>}
                </p>
                <p className="text-xs text-gray-500">
                  {matchingMode ? 'Candidates ranked by THEJMA algorithm for your selected job' : 'Enable to see match scores for a specific job posting'}
                </p>
              </div>
            </div>
            <div
              className={`relative w-14 h-7 rounded-full transition-all duration-300 shrink-0 ${
                matchingMode ? 'bg-indigo-600' : 'bg-gray-300'
              }`}
              onClick={e => { e.stopPropagation(); setMatchingMode(!matchingMode) }}
            >
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-all duration-300 ${
                matchingMode ? 'left-7' : 'left-0.5'
              }`} />
            </div>
          </div>

          {/* Job selector + stats (when active) */}
          {matchingMode && (
            <div className="px-4 pb-4 -mt-1 space-y-3" onClick={e => e.stopPropagation()}>
              {/* Job selector */}
              <div>
                <label className="block text-xs font-semibold text-indigo-700 mb-1.5">Select a job to match against:</label>
                <button
                  onClick={() => setShowJobPicker(true)}
                  className="w-full flex items-center justify-between border border-indigo-200 rounded-xl px-3 py-2.5 text-sm bg-white hover:bg-indigo-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-left transition-colors"
                >
                  <span className={selectedJobId ? 'text-gray-900' : 'text-gray-400'}>
                    {selectedJob ? selectedJob.title : 'Choose a job posting...'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-indigo-400 shrink-0" />
                </button>
              </div>

              {/* Stats row */}
              {matchStats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  <div className="bg-white/80 backdrop-blur rounded-xl p-3 text-center border border-indigo-100">
                    <p className="text-xl font-bold text-indigo-600">{matchStats.top}%</p>
                    <p className="text-[10px] text-gray-500 font-medium mt-0.5">Top Match</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-xl p-3 text-center border border-green-100">
                    <p className="text-xl font-bold text-green-600">{matchStats.excellent}</p>
                    <p className="text-[10px] text-gray-500 font-medium mt-0.5">Excellent (75%+)</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-xl p-3 text-center border border-blue-100">
                    <p className="text-xl font-bold text-blue-600">{matchStats.good}</p>
                    <p className="text-[10px] text-gray-500 font-medium mt-0.5">Good (50%+)</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-xl p-3 text-center border border-gray-100">
                    <p className="text-xl font-bold text-gray-600">{matchStats.avg}%</p>
                    <p className="text-[10px] text-gray-500 font-medium mt-0.5">Avg Score</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══════════ Search Bar ══════════ */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, program, or skills..."
              className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="lg:hidden flex items-center gap-1.5 px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
          >
            {matchingMode ? <Sparkles className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}
            {matchingMode ? 'Match Info' : 'Filters'}
          </button>
        </div>
      </div>

      {/* ══════════ Mobile filters (collapsible) ══════════ */}
      {showMobileFilters && (
        <div className={`lg:hidden rounded-2xl border shadow-sm p-5 ${
          matchingMode ? 'bg-gradient-to-b from-indigo-50 to-white border-indigo-200' : 'bg-white border-gray-200'
        }`}>
          {matchingMode ? renderMatchDashboard() : renderFilters()}
        </div>
      )}

      {/* ══════════ Main Layout: Sidebar + Grid ══════════ */}
      <div className="flex gap-6">
        {/* Left sidebar — desktop only */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className={`rounded-2xl border shadow-sm p-5 sticky top-6 transition-all duration-300 ${
            matchingMode
              ? 'bg-gradient-to-b from-indigo-50 to-white border-indigo-200'
              : 'bg-white border-gray-200'
          }`}>
            {matchingMode ? renderMatchDashboard() : renderFilters()}
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Loading state */}
          {(browseLoading || matchLoading) && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className={`animate-spin rounded-full h-10 w-10 border-b-2 ${matchingMode ? 'border-indigo-600' : 'border-blue-900'}`} />
              <p className="text-sm text-gray-500 mt-4">
                {matchLoading ? 'Computing THEJMA match scores...' : 'Loading alumni...'}
              </p>
            </div>
          )}

          {/* Match error */}
          {matchError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Failed to compute match scores</p>
                <p className="text-xs text-red-600 mt-0.5">{matchError}</p>
              </div>
            </div>
          )}

          {/* Matching mode but no job selected */}
          {matchingMode && !selectedJobId && !matchLoading && (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-indigo-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-700 mb-2">Select a Job Posting</h3>
              <p className="text-sm text-gray-400">Choose a job above to see candidates ranked with THEJMA match scores.</p>
            </div>
          )}

          {/* Empty state */}
          {!browseLoading && !matchLoading && !matchError && filteredAlumni.length === 0 && (matchingMode ? selectedJobId : true) && (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-700 mb-2">No candidates found</h3>
              <p className="text-sm text-gray-400">Try adjusting your search or filters.</p>
            </div>
          )}

          {/* Results count */}
          {!browseLoading && !matchLoading && filteredAlumni.length > 0 && (
            <p className="text-xs text-gray-500 mb-3">
              Showing {Math.min((candidatePage - 1) * CANDIDATES_PER_PAGE + 1, filteredAlumni.length)}–{Math.min(candidatePage * CANDIDATES_PER_PAGE, filteredAlumni.length)} of {filteredAlumni.length} candidate{filteredAlumni.length !== 1 ? 's' : ''}
              {matchingMode && selectedJob && <span className="text-indigo-600 font-medium"> · Matched for: {selectedJob.title}</span>}
            </p>
          )}

          {/* ── Alumni / Candidate cards (grid) ── */}
          {!browseLoading && !matchLoading && filteredAlumni.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {pagedAlumni.map(alumni => {
                const id = getId(alumni)
                const isCand = isCandidate(alumni)
                const candidate = isCand ? alumni : null
                const skills = alumni.skills_acquired ?? []
                const letter = (alumni.first_name?.charAt(0) || '?').toUpperCase()

                // Deterministic avatar color
                const avatarColors = [
                  'bg-blue-900', 'bg-indigo-600', 'bg-emerald-600',
                  'bg-rose-600', 'bg-violet-600', 'bg-amber-600',
                ]
                const colorIdx = `${alumni.first_name}${alumni.last_name}`.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % avatarColors.length

                return (
                  <div
                    key={id}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-200 flex flex-col h-full group cursor-pointer"
                    onClick={() => openCandidateProfile(alumni)}
                  >
                    <div className="p-5 flex-1 flex flex-col">
                      {/* Top: avatar + name + badges */}
                      <div className="flex items-start gap-3.5">
                        <div className={`w-11 h-11 rounded-xl ${avatarColors[colorIdx]} flex items-center justify-center text-lg font-bold shrink-0 overflow-hidden`}>
                          {alumni.profile_picture ? (
                            <img src={alumni.profile_picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white">{letter}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 leading-snug line-clamp-1 group-hover:text-blue-900 transition-colors">
                            {alumni.first_name} {alumni.last_name}
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5 truncate flex items-center gap-1">
                            <GraduationCap className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {alumni.program}
                          </p>
                        </div>
                        {candidate?.is_invited && (
                          <span className="shrink-0 bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded-full">INVITED</span>
                        )}
                        {!candidate?.is_invited && candidate?.application_status === 'hired' && (
                          <span className="shrink-0 bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full">HIRED</span>
                        )}
                        {!candidate?.is_invited && candidate?.application_status === 'rejected' && (
                          <span className="shrink-0 bg-red-100 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-full">REJECTED</span>
                        )}
                        {!candidate?.is_invited && candidate?.application_status != null
                          && candidate.application_status !== 'hired' && candidate.application_status !== 'rejected' && (
                          <span className="shrink-0 bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full">APPLIED</span>
                        )}
                      </div>

                      {/* Info row */}
                      <div className="flex items-center gap-2 mt-3 text-xs text-gray-600 flex-wrap">
                        {alumni.department && <span className="font-medium">{alumni.department}</span>}
                        {alumni.department && alumni.year_graduated > 0 && <span className="text-gray-300">·</span>}
                        {alumni.year_graduated > 0 && <span>Class of {alumni.year_graduated}</span>}
                        {alumni.current_employment_status && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className={`font-medium ${alumni.current_employment_status === 'employed' ? 'text-green-700' : alumni.current_employment_status === 'unemployed' ? 'text-red-600' : 'text-gray-600'}`}>
                              {STATUS_LABELS[alumni.current_employment_status] || alumni.current_employment_status}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Match score (AI mode) */}
                      {candidate && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="font-medium text-gray-600">Match Score</span>
                            <span className={`font-bold ${candidate.overall_score >= 75 ? 'text-green-600' : candidate.overall_score >= 50 ? 'text-blue-600' : 'text-gray-500'}`}>
                              {candidate.overall_score}%
                            </span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${candidate.overall_score >= 75 ? 'bg-green-500' : candidate.overall_score >= 50 ? 'bg-blue-500' : 'bg-gray-400'}`}
                              style={{ width: `${candidate.overall_score}%` }}
                            />
                          </div>
                          {!candidate.has_outcome_scores && (
                            <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-0.5">
                              <AlertTriangle className="w-2.5 h-2.5" /> Limited match data
                            </p>
                          )}
                        </div>
                      )}

                      {/* Field of work (browse mode) */}
                      {!matchingMode && alumni.field_of_work && (
                        <p className="text-sm text-gray-600 mt-3 line-clamp-1 leading-relaxed">
                          {alumni.field_of_work}
                        </p>
                      )}

                      {/* Skills tags */}
                      {skills.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {skills.slice(0, 4).map(skill => {
                            const isMatching = isSkillMatch(skill, candidate?.matching_skills)
                            return isMatching ? (
                              <span key={skill} className="bg-green-100 text-green-700 text-[11px] font-semibold px-2 py-0.5 rounded-md flex items-center gap-0.5">
                                <Star className="w-2.5 h-2.5" />{skill}
                              </span>
                            ) : (
                              <span key={skill} className="bg-gray-100 text-gray-700 text-[11px] font-medium px-2 py-0.5 rounded-md">{skill}</span>
                            )
                          })}
                          {skills.length > 4 && (
                            <span className="bg-gray-100 text-gray-500 text-[11px] font-medium px-2 py-0.5 rounded-md">+{skills.length - 4}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Footer: actions */}
                    <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {alumni.year_graduated > 0 ? `Graduated ${alumni.year_graduated}` : alumni.program}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={e => { e.stopPropagation(); openCandidateProfile(alumni) }}
                          className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-50 transition flex items-center gap-1.5"
                        >
                          <Eye className="w-3 h-3" /> View
                        </button>
                        {isCand && candidate && !candidate.is_invited && candidate.application_status == null && (
                          <button
                            onClick={e => { e.stopPropagation(); setInviteTarget(candidate); setInviteMessage('') }}
                            className="px-3.5 py-1.5 bg-blue-900 text-white text-xs font-semibold rounded-lg hover:bg-blue-800 transition flex items-center gap-1.5"
                          >
                            <Send className="w-3 h-3" /> Invite
                          </button>
                        )}
                        {!isCand && !invitedAlumniIds.has(id) && (
                          <button
                            onClick={e => { e.stopPropagation(); setBrowseInviteTarget(alumni as AlumniProfile); setInviteMessage(''); setBrowseInviteJobId('') }}
                            className="px-3.5 py-1.5 bg-blue-900 text-white text-xs font-semibold rounded-lg hover:bg-blue-800 transition flex items-center gap-1.5"
                          >
                            <Send className="w-3 h-3" /> Invite
                          </button>
                        )}
                        {!isCand && invitedAlumniIds.has(id) && (
                          <span className="text-[11px] text-purple-700 font-semibold px-2">Invited</span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* ── Pagination controls ── */}
          {!browseLoading && !matchLoading && totalCandidatePages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setCandidatePage(p => Math.max(1, p - 1))}
                disabled={candidatePage === 1}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                ← Prev
              </button>
              {Array.from({ length: totalCandidatePages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalCandidatePages || Math.abs(p - candidatePage) <= 2)
                .reduce<(number | '…')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('…')
                  acc.push(p)
                  return acc
                }, [])
                .map((p, i) =>
                  p === '…' ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCandidatePage(p as number)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                        p === candidatePage
                          ? 'bg-blue-900 text-white border-blue-900 font-semibold'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )
              }
              <button
                onClick={() => setCandidatePage(p => Math.min(totalCandidatePages, p + 1))}
                disabled={candidatePage === totalCandidatePages}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ══════════ Profile Modal ══════════ */}
      {createPortal(<>
      {viewingAlumni && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setViewingAlumni(null); setViewingAlumniEmail(undefined) }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <h2 className="text-lg font-bold text-gray-900">Candidate Profile</h2>
              <button onClick={() => { setViewingAlumni(null); setViewingAlumniEmail(undefined) }} className="p-1.5 hover:bg-gray-100 rounded-lg transition">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 p-6 space-y-5">
              {/* Avatar + name — centered layout */}
              <div className="flex flex-col items-center text-center gap-3">
                <div className="w-28 h-28 rounded-2xl bg-blue-900 flex items-center justify-center overflow-hidden">
                  {viewingAlumni.profile_picture ? (
                    <img src={viewingAlumni.profile_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-12 h-12 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 leading-tight">{viewingAlumni.first_name} {viewingAlumni.last_name}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">{programMap[viewingAlumni.program] || viewingAlumni.program}</p>
                  {viewingAlumni.department && (
                    <p className="text-xs text-gray-400 mt-0.5">{viewingAlumni.department}</p>
                  )}
                </div>
                {isCandidate(viewingAlumni) && (
                  <div className={`px-4 py-2 rounded-xl flex items-center gap-2 ${
                    viewingAlumni.overall_score >= 75 ? 'bg-green-50 border border-green-200' :
                    viewingAlumni.overall_score >= 50 ? 'bg-blue-50 border border-blue-200' :
                    'bg-gray-50 border border-gray-200'
                  }`}>
                    <p className={`text-lg font-bold ${
                      viewingAlumni.overall_score >= 75 ? 'text-green-600' :
                      viewingAlumni.overall_score >= 50 ? 'text-blue-600' : 'text-gray-500'
                    }`}>{viewingAlumni.overall_score}%</p>
                    <p className="text-xs text-gray-400 font-medium">Match</p>
                  </div>
                )}
              </div>

              {/* Match score breakdown (AI mode only) */}
              {isCandidate(viewingAlumni) && (
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-indigo-500" /> THEJMA Score Breakdown
                  </h4>
                  {!viewingAlumni.has_outcome_scores && (
                    <div className="flex items-center gap-2 text-amber-700 bg-amber-50 px-3 py-2 rounded-lg text-xs">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Limited match data — alumni hasn't completed PEO/PO assessment.</span>
                    </div>
                  )}
                  <div className="space-y-2.5">
                    {[
                      { label: `${viewingAlumni.score_type} Alignment`, score: viewingAlumni.peo_alignment_score, tooltip: 'Measures how well this candidate\'s academic outcomes align with this job\'s requirements. PEOs (Program Educational Objectives) are broad career goals graduates achieve years after graduation; POs (Program Outcomes) are specific skills and competencies demonstrated upon completing their program.' },
                      { label: 'Skills Match', score: viewingAlumni.skills_score },
                      { label: 'Profile Fit', score: viewingAlumni.profile_fit_score },
                    ].map(item => (
                      <div key={item.label}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-600 flex items-center gap-1">{item.label}{'tooltip' in item && <InfoTooltip text={(item as any).tooltip} />}</span>
                          <span className="font-bold text-gray-700">{item.score}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${barColor(item.score)}`} style={{ width: `${item.score}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact info */}
              {(viewingAlumni.phone || viewingAlumniEmail !== null) && (
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Contact</h4>
                  <div className="space-y-2">
                    {viewingAlumni.phone && (
                      <div className="flex items-center gap-2.5 text-sm text-gray-700">
                        <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                        {viewingAlumni.phone}
                      </div>
                    )}
                    {viewingAlumniEmail === undefined ? (
                      <div className="flex items-center gap-2.5 text-sm text-gray-400">
                        <Mail className="w-4 h-4 shrink-0" />
                        <span className="italic">Loading…</span>
                      </div>
                    ) : viewingAlumniEmail ? (
                      <div className="flex items-center gap-2.5 text-sm text-gray-700">
                        <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                        <a href={`mailto:${viewingAlumniEmail}`} className="hover:text-blue-600 transition">{viewingAlumniEmail}</a>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}

              {/* Professional info — 2-col grid */}
              <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Professional Info</h4>
                <div className="grid grid-cols-2 gap-2">
                  {viewingAlumni.year_graduated > 0 && (
                    <div className="flex items-center gap-2.5 bg-gray-50 rounded-lg px-3 py-2.5">
                      <GraduationCap className="w-4 h-4 text-blue-600 shrink-0" />
                      <div>
                        <p className="text-[10px] text-gray-400">Graduated</p>
                        <p className="text-sm font-medium text-gray-900">{viewingAlumni.year_graduated}</p>
                      </div>
                    </div>
                  )}
                  {viewingAlumni.current_employment_status && (
                    <div className="flex items-center gap-2.5 bg-gray-50 rounded-lg px-3 py-2.5">
                      <Users className="w-4 h-4 text-emerald-600 shrink-0" />
                      <div>
                        <p className="text-[10px] text-gray-400">Status</p>
                        <p className="text-sm font-medium text-gray-900">{STATUS_LABELS[viewingAlumni.current_employment_status] || viewingAlumni.current_employment_status}</p>
                      </div>
                    </div>
                  )}
                  {viewingAlumni.field_of_work && (
                    <div className="col-span-2 flex items-center gap-2.5 bg-gray-50 rounded-lg px-3 py-2.5">
                      <Target className="w-4 h-4 text-purple-600 shrink-0" />
                      <div>
                        <p className="text-[10px] text-gray-400">Field of Work</p>
                        <p className="text-sm font-medium text-gray-900">{viewingAlumni.field_of_work}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Skills */}
              {(viewingAlumni.skills_acquired ?? []).length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Skills</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {(viewingAlumni.skills_acquired ?? []).map(skill => {
                      const isMatch = isCandidate(viewingAlumni) && isSkillMatch(skill, viewingAlumni.matching_skills)
                      return (
                        <span key={skill} className={`px-2.5 py-1 text-xs font-medium rounded-lg border ${
                          isMatch ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-600 border-gray-200'
                        }`}>
                          {isMatch && <Star className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />}
                          {skill}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Career Timeline */}
              <CareerTimeline
                alumniId={isCandidate(viewingAlumni) ? viewingAlumni.alumni_id : (viewingAlumni as AlumniProfile).user_id}
                canEdit={false}
              />

              {/* Matching outcomes (AI mode) */}
              {isCandidate(viewingAlumni) && viewingAlumni.matching_outcomes.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1">Matching {viewingAlumni.score_type}s <InfoTooltip text="These are the specific academic outcomes from this candidate's program that directly match the requirements of this job." /></h4>
                  <ul className="space-y-1.5">
                    {viewingAlumni.matching_outcomes.map(outcome => (
                      <li key={outcome} className="flex items-start gap-2 text-sm text-gray-600">
                        <Star className="w-3.5 h-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex items-center justify-between gap-3 shrink-0">
              <button
                onClick={() => { setViewingAlumni(null); setViewingAlumniEmail(undefined) }}
                className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-sm font-medium text-gray-700"
              >
                Close
              </button>
              {isCandidate(viewingAlumni) ? (
                viewingAlumni.is_invited ? (
                  <span className="text-sm text-purple-700 font-medium">Invitation sent</span>
                ) : viewingAlumni.application_status === 'hired' ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg">
                    <CheckCircle className="w-4 h-4" /> Hired for this job
                  </span>
                ) : viewingAlumni.application_status === 'rejected' ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-red-600 font-medium bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg">
                    <XCircle className="w-4 h-4" /> Application rejected
                  </span>
                ) : viewingAlumni.application_status != null ? (
                  <span className="text-sm text-blue-700 font-medium">Already applied</span>
                ) : (
                  <button
                    onClick={() => { setInviteTarget(viewingAlumni); setInviteMessage(''); setViewingAlumni(null); setViewingAlumniEmail(undefined) }}
                    className="flex items-center gap-2 px-5 py-2.5 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition font-semibold text-sm"
                  >
                    <Send className="w-4 h-4" /> Invite to Apply
                  </button>
                )
              ) : !invitedAlumniIds.has((viewingAlumni as AlumniProfile).user_id) ? (
                <button
                  onClick={() => { setBrowseInviteTarget(viewingAlumni as AlumniProfile); setInviteMessage(''); setBrowseInviteJobId(''); setViewingAlumni(null); setViewingAlumniEmail(undefined) }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition font-semibold text-sm"
                >
                  <Send className="w-4 h-4" /> Invite to Apply
                </button>
              ) : (
                <span className="text-sm text-purple-700 font-medium">Invitation sent</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════ Job Picker Modal ══════════ */}
      {showJobPicker && (() => {
        const iconColors = [
          'bg-blue-900 text-white', 'bg-yellow-400 text-gray-900', 'bg-indigo-600 text-white',
          'bg-emerald-600 text-white', 'bg-rose-600 text-white', 'bg-violet-600 text-white',
        ]
        const filteredPickerJobs = jobs.filter(j =>
          j.title.toLowerCase().includes(jobPickerSearch.toLowerCase())
        )
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowJobPicker(false); setJobPickerSearch('') }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Select a Job Posting</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{jobs.length} active job{jobs.length !== 1 ? 's' : ''}</p>
                </div>
                <button onClick={() => { setShowJobPicker(false); setJobPickerSearch('') }} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Search bar */}
              <div className="px-4 py-3 border-b shrink-0">
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    autoFocus
                    value={jobPickerSearch}
                    onChange={e => setJobPickerSearch(e.target.value)}
                    placeholder="Search jobs..."
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Job list */}
              <div className="overflow-y-auto flex-1 p-4 space-y-3">
                {filteredPickerJobs.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">{jobPickerSearch ? 'No jobs match your search.' : 'No active job postings found.'}</p>
                  </div>
                ) : (
                  filteredPickerJobs.map(job => {
                    const colorIdx = job.title.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % iconColors.length
                    const isSelected = selectedJobId === job.id
                    return (
                      <button
                        key={job.id}
                        onClick={() => { setSelectedJobId(job.id); setShowJobPicker(false); setJobPickerSearch('') }}
                        className={`w-full text-left rounded-2xl border transition-all duration-150 ${
                          isSelected
                            ? 'border-indigo-400 bg-indigo-50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-indigo-200 hover:shadow-md'
                        }`}
                      >
                        <div className="p-4">
                          {/* Header row */}
                          <div className="flex items-start gap-3">
                            <div className={`w-11 h-11 rounded-xl ${iconColors[colorIdx]} flex items-center justify-center text-lg font-bold shrink-0`}>
                              {job.title.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className={`font-bold leading-snug truncate ${isSelected ? 'text-indigo-700' : 'text-gray-900'}`}>
                                  {job.title}
                                </h3>
                                {isSelected && (
                                  <span className="bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">SELECTED</span>
                                )}
                                {job.is_partner_listing && (
                                  <span className="bg-yellow-400 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5 shrink-0">
                                    <Star className="w-3 h-3" /> PARTNER
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Location */}
                          {job.location && (
                            <p className="text-xs text-gray-500 mt-2.5 flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5 text-gray-400" /> {job.location}
                            </p>
                          )}

                          {/* Info row */}
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-600 flex-wrap">
                            <span className="font-medium">{EXPERIENCE_LEVEL_LABELS[job.experience_level]}</span>
                            <span className="text-gray-300">·</span>
                            <span>{EMPLOYMENT_TYPE_LABELS[job.employment_type]}</span>
                            {job.salary_range && (
                              <>
                                <span className="text-gray-300">·</span>
                                <span className="text-green-700 font-medium">{SALARY_LABELS[job.salary_range as SalaryRange] || job.salary_range}</span>
                              </>
                            )}
                          </div>

                          {/* Description snippet */}
                          {job.description && (
                            <p className="text-sm text-gray-600 mt-2.5 line-clamp-2 leading-relaxed">
                              {job.description}
                            </p>
                          )}

                          {/* Skills tags */}
                          {(job.skills_required?.length || 0) > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2.5">
                              {job.skills_required.slice(0, 5).map(s => (
                                <span key={s} className="bg-gray-100 text-gray-700 text-[11px] font-medium px-2 py-0.5 rounded-md">{s}</span>
                              ))}
                              {job.skills_required.length > 5 && (
                                <span className="bg-gray-100 text-gray-500 text-[11px] font-medium px-2 py-0.5 rounded-md">+{job.skills_required.length - 5}</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Card footer */}
                        {job.deadline && (
                          <div className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-orange-400" />
                            <span className="text-xs text-orange-600 font-medium">
                              Deadline: {new Date(job.deadline).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        )}
                      </button>
                    )
                  })
                )}
              </div>
            </div>
          </div>
        )
      })()}

      {/* ══════════ Invite Modal ══════════ */}
      {inviteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">Invite to Apply</h2>
              <button onClick={() => setInviteTarget(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                <div className="w-10 h-10 rounded-full bg-blue-900 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {inviteTarget.profile_picture ? (
                    <img src={inviteTarget.profile_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{inviteTarget.first_name} {inviteTarget.last_name}</p>
                  <p className="text-xs text-gray-500">{inviteTarget.program} · {inviteTarget.overall_score}% match</p>
                </div>
              </div>
              {selectedJob && (
                <div className="flex items-center gap-2 bg-indigo-50 rounded-lg px-3 py-2 text-xs text-indigo-700">
                  <Briefcase className="w-3.5 h-3.5" />
                  <span className="font-medium">For:</span> {selectedJob.title}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Personal Message <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={inviteMessage}
                  onChange={e => setInviteMessage(e.target.value)}
                  maxLength={500}
                  rows={4}
                  placeholder="Add a personal message to the candidate..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{inviteMessage.length}/500</p>
              </div>
              <p className="text-xs text-gray-500">
                The candidate will receive an in-app notification and email about this invitation.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button onClick={() => setInviteTarget(null)} className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-sm font-medium">
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={inviting}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition text-sm font-bold disabled:opacity-50"
              >
                <Send className="w-4 h-4" /> {inviting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ Browse Invite Modal ══════════ */}
      {browseInviteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">Invite to Apply</h2>
              <button onClick={() => setBrowseInviteTarget(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Alumni info card */}
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                <div className="w-10 h-10 rounded-full bg-blue-900 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {browseInviteTarget.profile_picture ? (
                    <img src={browseInviteTarget.profile_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{browseInviteTarget.first_name} {browseInviteTarget.last_name}</p>
                  <p className="text-xs text-gray-500">{browseInviteTarget.program}</p>
                </div>
              </div>

              {/* Job selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Job Posting <span className="text-red-500">*</span>
                </label>
                {jobs.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No active job postings. Create a job first.</p>
                ) : (
                  <select
                    value={browseInviteJobId}
                    onChange={e => setBrowseInviteJobId(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value="">Choose a job posting...</option>
                    {jobs.map(j => (
                      <option key={j.id} value={j.id}>{j.title}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Already-invited warning */}
              {browseAlreadyInvited && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
                  <span>This alumni has already been invited for this job and the invitation is still active.</span>
                </div>
              )}

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Personal Message <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={inviteMessage}
                  onChange={e => setInviteMessage(e.target.value)}
                  maxLength={500}
                  rows={4}
                  placeholder="Add a personal message to the candidate..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{inviteMessage.length}/500</p>
              </div>
              <p className="text-xs text-gray-500">
                The candidate will receive an in-app notification and email about this invitation.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button onClick={() => setBrowseInviteTarget(null)} className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-sm font-medium">
                Cancel
              </button>
              <button
                onClick={handleBrowseInvite}
                disabled={browseInviting || !browseInviteJobId || browseAlreadyInvited}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition text-sm font-bold disabled:opacity-50"
              >
                <Send className="w-4 h-4" /> {browseInviting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>, document.body)}
    </div>
  )
}

export default EmployerFindCandidatesPage
